/**
 * Session transport: who the current admin is, exchanging a Firebase token for a cookie, and
 * signing out.
 *
 * ⚠️ WHY `src/shared/` AND NOT A FEATURE, WHICH IS WHERE T68 PUTS EVERY OTHER `api.ts`.
 *
 * `useAuth` has three consumers and two of them are legacy: `src/components/AdminLayout.tsx:41`
 * and `src/pages/Login.tsx:14`, plus `src/main.tsx`. A legacy file may import only legacy, shared,
 * domain and contracts — never a feature — so `src/features/auth/api.ts` would be unreachable from
 * the two screens that need it. Moving the hook is blocked twice over: those consumers would have
 * to move first (that is T71, the app-shell move), and `src/hooks/useAuth.test.tsx` pins the hook
 * at `src/hooks/` by import path.
 *
 * `src/api/auth.ts` is the other candidate and is what T68's scope names. It is declined: that
 * directory is the legacy layer this migration exists to empty, and a module put there would have
 * to move a second time when the shell does.
 *
 * `src/shared/` is reachable from every layer and is defined as infrastructure that knows about no
 * screen — which this is. When `AdminLayout` and `Login` become features, nothing here changes.
 */
import { http } from '@/shared/http';
import type { AdminUser } from '@/contracts/admin-user';

const authApi = {
    /** The session probe. A rejection means "not signed in", not "the API is down" — see F-007. */
    me: () => http.get<AdminUser>('/backoffice/auth/me'),

    /**
     * ⚠️ THE ONLY BEARER HEADER IN THE APP, AND IT IS ONE-OFF BY DESIGN.
     *
     * The Firebase ID token is exchanged for an HTTP-only session cookie on this single request;
     * every later call rides the cookie. Nothing is persisted — two guards assert
     * `localStorage.nexvia_admin_token` is null after both a sign-in and a session restore (F-006).
     *
     * `credentials: 'include'` still matters here even though the caller is unauthenticated: it is
     * what lets the server SET the cookie on the response, and a guard pins it.
     */
    exchangeFirebaseToken: (idToken: string) =>
        http.post<{ success: boolean; user: AdminUser }>('/backoffice/auth/firebase', null, {
            headers: { Authorization: `Bearer ${idToken}` },
        }),

    logout: () => http.post('/backoffice/auth/logout'),
};

export default authApi;
